import { Box, Button } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { ButtonsBlockProps } from '../types';
import * as styles from './ButtonsBlock.styles';

const ButtonsBlock = ({
  activeStep,
  stepsCount,
  setActiveStep,
  handleSubmit,
  currentSchema,
}: ButtonsBlockProps) => {
  const { t } = useTranslation();

  const { trigger, reset } = useFormContext();

  const handleNext = async () => {
    const isStepValid = await trigger();
    if (isStepValid) setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const hasRequiredFields = () => {
    // If there is no schema, then all fields are required
    if (!currentSchema) return true;
    return Object.values(currentSchema.shape).some((field) => {
      return !(field instanceof z.ZodOptional); // Not optional means required
    });
  };

  // eslint-disable-next-line
  const getEmptyValues = (schema: z.ZodObject<any>) => {
    return Object.keys(schema.shape).reduce(
      (acc, key) => {
        const fieldType = schema.shape[key];
        // Handle different types (string, number, etc.)
        if (fieldType instanceof z.ZodString) {
          acc[key] = ''; // Set strings to empty
        } else if (fieldType instanceof z.ZodNumber) {
          acc[key] = undefined; // Set numbers to undefined
        } else {
          acc[key] = null; // Fallback for other types (arrays, objects, etc.)
        }
        return acc;
      },
      // eslint-disable-next-line
      {} as Record<string, any>
    );
  };

  const handleSkip = () => {
    if (currentSchema) {
      const emptyValues = getEmptyValues(currentSchema);
      reset(emptyValues); // Reset the form with empty values for current step
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  return (
    <Box sx={styles.blockWrap} data-cy="buttons-block">
      {activeStep > 0 && (
        <Button
          variant="outlined"
          size="large"
          sx={styles.back}
          onClick={handleBack}
          data-cy="back"
        >
          {t('common.back', {
            defaultValue: 'Back',
          })}
        </Button>
      )}

      {!hasRequiredFields() && activeStep !== stepsCount - 1 && (
        <Button
          variant="outlined"
          size="large"
          sx={styles.skip}
          data-cy="skip"
          onClick={handleSkip}
        >
          {t('common.skip', {
            defaultValue: 'Skip',
          })}
        </Button>
      )}

      {activeStep !== stepsCount - 1 ? (
        <Button
          variant="contained"
          size="large"
          sx={styles.next}
          data-cy="continue"
          onClick={handleNext}
        >
          {t('common.continue', {
            defaultValue: 'Continue',
          })}
        </Button>
      ) : (
        <Button
          variant="contained"
          size="large"
          sx={styles.next}
          data-cy="done"
          onClick={handleSubmit}
        >
          {t('common.done', {
            defaultValue: 'DONE',
          })}
        </Button>
      )}
    </Box>
  );
};

export default ButtonsBlock;
